import PageSEO from '@/components/PageSEO';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Phone, Wine } from 'lucide-react';
import ProudlyServingSection from '@/components/ProudlyServingSection';

export const WineEnclosuresPage = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Wine Cellar Glass Enclosure Installation",
    "name": "Custom Glass Wine Enclosures",
    "description": "Custom glass wine cellar enclosures with temperature control, UV protection, and thermally-broken seals for optimal wine storage.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Comal Glass Company",
      "telephone": "+1-830-359-9925"
    },
    "areaServed": { "@type": "State", "name": "Texas" }
  };

  // FAQ data for wine enclosures
  const wineFaqs = [
    {
      question: "What type of glass is best for a wine cellar enclosure?",
      answer: "For wine cellars, we recommend dual-pane insulated glass units (IGUs) with Low-E coating. The dual-pane construction with argon gas fill provides excellent thermal insulation, while the Low-E coating blocks 99% of UV rays that can damage wine. For extreme temperature differentials, triple-pane IGUs are available."
    },
    {
      question: "What is a thermally-broken frame and why is it important?",
      answer: "A thermally-broken frame has a polyamide barrier between the interior and exterior aluminum sections. This prevents temperature transfer and eliminates condensation on the glass. Without thermal breaks, cold cellar air meeting warm room air causes moisture buildup that can damage wine labels and promote mold."
    },
    {
      question: "Can glass wine cellars maintain proper temperature and humidity?",
      answer: "Yes, when properly designed with insulated glass, thermally-broken frames, and quality seals, glass wine cellars can maintain the ideal 55°F temperature and 60-70% humidity. We work with leading cooling system manufacturers like WhisperKOOL, CellarPro, and Wine Guardian to ensure compatibility."
    },
    {
      question: "How much space do I need for a wine cellar?",
      answer: "Glass wine enclosures can be designed for spaces as small as a closet (holding 100-200 bottles) up to full walk-in cellars with thousands of bottles. Under-stair conversions are popular options. We custom-design each enclosure to maximize your specific space."
    },
    {
      question: "What hardware finishes are available for wine cellar glass doors?",
      answer: "We offer hardware in polished chrome, brushed nickel, matte black, oil-rubbed bronze, and satin brass. All hardware is rated for the temperature and humidity conditions of wine cellars. Door options include hinged, sliding, and pivot configurations."
    },
    {
      question: "Does UV-protective glass affect the appearance of the wine cellar?",
      answer: "No, modern Low-E coatings are virtually invisible and don't affect the clarity or color of the glass. Your wine collection will be displayed beautifully while being protected from harmful UV light that can prematurely age wine."
    }
  ];

  // FAQ Schema for AI engines
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": wineFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const features = [
    'Custom-sized glass doors and panels',
    'Temperature-controlled designs',
    'Various glass thickness options',
    'Multiple hardware finishes',
    'Seamless frameless appearance',
    'UV-protective glass options',
    'Climate seal integration',
    'LED lighting integration ready',
  ];

  const benefits = [
    {
      title: 'Temperature Control',
      description: 'Thermally-broken, properly sealed glass enclosures help maintain optimal wine storage temperatures.',
    },
    {
      title: 'UV Protection',
      description: 'Protect your collection from harmful UV rays that can damage wine quality.',
    },
    {
      title: 'Display Your Collection',
      description: 'Showcase your wine collection as a stunning focal point in your home.',
    },
    {
      title: 'Increase Home Value',
      description: 'A custom wine cellar adds luxury and value to your property.',
    },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="Wine Room Glass Enclosures" description="Custom glass wine cellars and enclosures with temperature control, UV protection, and frameless designs. Serving builders and homeowners across the Texas Hill Country." path="/wine-enclosures" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/services/wine_room.jpg"
            alt="Wine cellar enclosure"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        <div className="container-custom relative z-10 text-white">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 backdrop-blur-sm">
              Luxury Wine Storage
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Custom <span className="text-gradient-cyan">Wine Enclosures</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
              Elegant glass wine cellars and storage solutions designed for discerning collectors. Preserve your collection in style with custom glass enclosures.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Premium Features</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Designed for <span className="text-gradient-cyan">Collectors</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every detail is carefully considered to protect and showcase your wine collection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                    <p className="text-sm">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">The Benefits</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Why Glass <span className="text-gradient-cyan">Wine Enclosures?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <Wine className="h-10 w-10 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Design Options Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">Design Options</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Customized to Your <span className="text-gradient-cyan">Vision</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Full Room Enclosures', description: 'Complete glass walls for dedicated wine cellars with climate control systems.' },
              { title: 'Under-Stair Storage', description: 'Maximize unused space with custom-fit glass wine storage solutions.' },
              { title: 'Display Cabinets', description: 'Freestanding or built-in glass cabinets for showcasing your finest bottles.' },
            ].map((option, index) => (
              <Card key={index} className="border-none bg-card">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold mb-2">{option.title}</h3>
                  <p className="text-muted-foreground text-sm">{option.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Recent <span className="text-gradient-cyan">Wine Cellars</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              '/images/portfolio/Image001.jpg',
              '/images/portfolio/Image004.jpg',
              '/images/services/wine_glass_doors.jpeg',
            ].map((image, index) => (
              <div key={index} className="aspect-square overflow-hidden rounded-lg shadow-lg">
                <img src={image} alt={`Wine cellar ${index + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button variant="outline" size="lg" asChild>
              <Link to="/portfolio">View All Projects</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Proudly Serving Section */}
      <ProudlyServingSection serviceSlug="wine-enclosures" />

      {/* FAQ Section with Schema */}
      <section className="py-20 bg-secondary/50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <Badge className="mb-4">Common Questions</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Wine Enclosure <span className="text-gradient-cyan">FAQs</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {wineFaqs.map((faq, index) => (
              <Card key={index} className="border-none shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Create Your Dream <span className="text-gradient-cyan">Wine Cellar</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Let's design a custom glass wine enclosure that perfectly preserves and displays your collection.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  (830) 359-9925
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WineEnclosuresPage;
